import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Alert, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';
import { fmtDateTZ } from '../../utils/date';

// Demandes de suppression de compte (usafiri.yt/delete-account.html), traitées
// par la fonction deletion-requests. Délai promis aux utilisateurs : 30 jours.
const DEADLINE_DAYS = 30;

const STATUS = (P) => ({
  pending:   { label: 'À traiter',     color: P.orange },
  done:      { label: 'Supprimé',      color: P.green },
  not_found: { label: 'Aucun compte',  color: P.blue },
  rejected:  { label: 'Rejetée',       color: P.muted },
});

const daysLeft = (createdAt) =>
  DEADLINE_DAYS - Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000);

export default function DeletionRequestsTab({ P, thSx, tdSx }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/deletion-requests');
      setRequests(data.requests || []);
    } catch (e) {
      setMessage({ severity: 'error', text: e.response?.data?.error || 'Impossible de charger les demandes.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const process = async (r, action) => {
    const who = r.users ? `${r.users.first_name || ''} ${r.users.last_name || ''}`.trim() || r.email : r.email;
    const question = action === 'delete'
      ? `Supprimer définitivement le compte de ${who} ?\n\nLe compte Usafiri est anonymisé, le client Stripe et le compte de connexion sont supprimés, puis un e-mail de confirmation est envoyé. C'est irréversible.`
      : `Rejeter la demande de ${r.email} ? (par exemple si elle ne vient pas du titulaire du compte)`;
    if (!window.confirm(question)) return;
    const note = action === 'reject' ? window.prompt('Raison du rejet (visible uniquement dans l\'admin) :', '') : undefined;
    if (action === 'reject' && note === null) return;

    setBusyId(r.id);
    setMessage(null);
    try {
      const { data } = await api.post(`/deletion-requests/${r.id}/process`, { action, note });
      setMessage({ severity: data.status === 'done' ? 'success' : 'info', text: data.note });
      await load();
    } catch (e) {
      setMessage({ severity: 'error', text: e.response?.data?.error || 'Erreur lors du traitement.' });
    } finally {
      setBusyId(null);
    }
  };

  const statuses = STATUS(P);
  const pending = requests.filter((r) => r.status === 'pending').length;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography sx={{ color: P.text, fontWeight: 700 }}>Demandes de suppression de compte</Typography>
          <Typography sx={{ color: P.soft, fontSize: '0.78rem', mt: 0.5 }}>
            {pending} à traiter · délai promis : {DEADLINE_DAYS} jours après la demande
          </Typography>
        </Box>
        <Button onClick={load} startIcon={<RefreshIcon />} size="small" sx={{ color: P.soft, textTransform: 'none' }}>
          Actualiser
        </Button>
      </Box>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress size={24} sx={{ color: P.gold }} /></Box>
      ) : requests.length === 0 ? (
        <Typography sx={{ color: P.soft, py: 6, textAlign: 'center' }}>Aucune demande pour l'instant.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Date</TableCell>
                <TableCell sx={thSx}>Demandeur</TableCell>
                <TableCell sx={thSx}>Compte trouvé</TableCell>
                <TableCell sx={thSx}>Raison</TableCell>
                <TableCell sx={thSx}>Statut</TableCell>
                <TableCell sx={thSx} align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => {
                const st = statuses[r.status] || statuses.pending;
                const left = daysLeft(r.created_at);
                return (
                  <TableRow key={r.id}>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', color: P.text }}>{fmtDateTZ(r.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
                      {r.status === 'pending' && (
                        <Typography sx={{ fontSize: '0.68rem', color: left <= 7 ? P.red : P.soft }}>
                          {left >= 0 ? `${left} j restants` : `en retard de ${-left} j`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.8rem', color: P.text }}>{r.email}</Typography>
                      {r.phone && <Typography sx={{ fontSize: '0.7rem', color: P.soft }}>{r.phone}</Typography>}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: '0.78rem', color: r.users ? P.text : P.muted }}>
                        {r.users ? `${r.users.first_name || ''} ${r.users.last_name || ''} (n° ${r.user_id}, ${r.users.user_type})` : 'non trouvé'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, maxWidth: 220 }}>
                      <Typography sx={{ fontSize: '0.75rem', color: P.soft }}>{r.reason || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title={r.note || ''}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: st.color }}>{st.label}</Typography>
                      </Tooltip>
                      {r.processed_at && (
                        <Typography sx={{ fontSize: '0.68rem', color: P.muted }}>le {fmtDateTZ(r.processed_at, { day: 'numeric', month: 'short' })}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx} align="right">
                      {r.status === 'pending' && (
                        busyId === r.id ? <CircularProgress size={18} sx={{ color: P.gold }} /> : (
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => process(r, 'reject')} sx={{ color: P.soft, textTransform: 'none' }}>Rejeter</Button>
                            <Button size="small" variant="contained" onClick={() => process(r, 'delete')}
                              sx={{ bgcolor: P.red, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>
                              Supprimer le compte
                            </Button>
                          </Box>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
